import React from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  House,
  Users,
  CalendarCheck,
  Gear,
  SignOut,
  Wrench,
  EnvelopeSimple,
  List,
} from '@phosphor-icons/react';

const navItems = [
  { to: '/service-os', icon: House, label: 'Dashboard', end: true },
  { to: '/service-os/leads', icon: Users, label: 'Leads' },
  { to: '/service-os/jobs', icon: CalendarCheck, label: 'Jobs' },
  { to: '/service-os/settings', icon: Gear, label: 'Settings' },
];

export default function SOSLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="flex h-screen bg-[#0B0C10] text-[#E2E8F0]" data-testid="sos-layout">
      {/* Sidebar */}
      <aside className={`
        fixed inset-y-0 left-0 z-40 w-64 bg-[#12131A] border-r border-[#1E1F2E] flex flex-col
        transition-transform duration-200
        lg:translate-x-0 lg:static
        ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex items-center gap-3 px-6 h-16 border-b border-[#1E1F2E]">
          <Wrench size={22} className="text-[#14F195]" weight="duotone" />
          <span className="font-semibold text-base tracking-tight">Service OS</span>
        </div>

        <nav className="flex-1 py-4 px-3 space-y-1">
          {navItems.map(({ to, icon: Icon, label, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-[#14F195]/10 text-[#14F195]'
                    : 'text-[#8B8FA3] hover:bg-[#1A1B26] hover:text-[#E2E8F0]'
                }`
              }
              data-testid={`sos-nav-${label.toLowerCase()}`}
            >
              <Icon size={20} weight="duotone" />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-[#1E1F2E]">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-full bg-[#6366F1]/20 flex items-center justify-center text-xs font-bold text-[#6366F1]">
              {(user?.name || user?.email || 'U')[0].toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name || 'User'}</p>
              <p className="text-xs text-[#8B8FA3] truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-xs text-[#8B8FA3] hover:text-red-400 transition-colors w-full"
            data-testid="sos-logout-btn"
          >
            <SignOut size={14} /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile top bar */}
        <div className="lg:hidden flex items-center gap-3 px-4 h-14 bg-[#12131A] border-b border-[#1E1F2E]">
          <button onClick={() => setMobileOpen(true)} data-testid="sos-mobile-menu">
            <List size={22} />
          </button>
          <Wrench size={18} className="text-[#14F195]" weight="duotone" />
          <span className="font-semibold text-sm">Service OS</span>
        </div>

        <main className="flex-1 overflow-y-auto p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
